import React from 'react';
import { useListForm } from '../../hooks/useListForm';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import Conditionals from '../Conditionals';
import { getAllEndingsFromNovel } from '../../utils/endingsHelper';
import './InteractiveSceneModifyingStates.css';
import CustomSelect from '../CustomSelect';

const INITIAL_DATA = { states: { flags: [], scales: [] }, conditionals: [], type: 'continuous' };

const InteractiveSceneModifyingStates = ({
  novelId, sceneId, scene, setScene, novel, loading, setLoading, error, setError, isProduction = false, sceneType = 'interactive'
}) => {
  const { showForm, editingIndex, formData, setFormData, openAdd, openEdit, close } = useListForm(INITIAL_DATA);
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading, sceneType);

  const handleEditModifyingStateClick = (modifyingState, index) => {
    openEdit({
      states: { flags: modifyingState.states?.flags || [], scales: modifyingState.states?.scales || [] },
      conditionals: modifyingState.conditionals || [],
      type: modifyingState.type || 'continuous'
    }, index);
  };

  const handleSaveModifyingState = async () => {
    if (formData.states.flags.length === 0 && formData.states.scales.length === 0) {
      setError('Добавьте хотя бы одну модификацию флага или шкалы');
      return;
    }
    setError(null);
    const updatedModifyingStates = [...(scene?.modifying_states || [])];
    const newModifyingState = { states: formData.states, conditionals: formData.conditionals, type: formData.type };
    if (editingIndex !== null) {
      updatedModifyingStates[editingIndex] = { ...updatedModifyingStates[editingIndex], ...newModifyingState };
    } else {
      updatedModifyingStates.push(newModifyingState);
    }
    const success = await saveScene({ ...scene, modifying_states: updatedModifyingStates }, 'Модификация состояния успешно сохранена!');
    if (success) close();
  };

  const handleDeleteModifyingState = async (index) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту модификацию состояния?')) return;
    const updatedModifyingStates = [...(scene?.modifying_states || [])];
    updatedModifyingStates.splice(index, 1);
    await saveScene({ ...scene, modifying_states: updatedModifyingStates }, 'Модификация состояния успешно удалена!');
  };

  const updateFlags = (flags) => setFormData({ ...formData, states: { ...formData.states, flags } });
  const updateScales = (scales) => setFormData({ ...formData, states: { ...formData.states, scales } });
  const updateFlag = (index, field, value) => {
    const flags = [...formData.states.flags];
    flags[index] = { ...flags[index], [field]: value };
    updateFlags(flags);
  };
  const updateScale = (index, field, value) => {
    const scales = [...formData.states.scales];
    scales[index] = { ...scales[index], [field]: value };
    updateScales(scales);
  };

  const getFlagName = (flagId) => novel?.states?.flags?.find(f => f.id === flagId)?.name || 'Неизвестный флаг';
  const getScaleName = (scaleId) => novel?.states?.scales?.find(s => s.id === scaleId)?.name || 'Неизвестная шкала';

  return (
    <div className="ismod-wrap">
      <div className="ismod-header">
        <h3 className="ismod-title">Модификации состояний</h3>
        <p className="ismod-help">Настройте, как состояния персонажей (флаги и шкалы) изменяются во время этой сцены</p>
      </div>

      <div className="ismod-add-wrap">
        <button className="ismod-add-btn" onClick={openAdd} disabled={loading}>
          <span className="button-icon">➕</span>
          Добавить модификацию состояния
        </button>
      </div>

      {error && <div className="ismod-error">{error}</div>}

      {showForm && (
        <div className="ismod-overlay">
          <div className="ismod-modal">
            <h3 className="ismod-modal-title">{editingIndex !== null ? 'Редактировать' : 'Добавить модификацию состояния'}</h3>

            <div className="ismod-form-group">
              <label className="ismod-label">Модификации состояний:</label>

              <div className="ismod-section">
                <div className="ismod-section-head">
                  <h4 className="ismod-section-title">Модификации флагов</h4>
                  <button type="button" onClick={() => updateFlags([...formData.states.flags, { flag_id: '', change_reason_prompt: '' }])} className="ismod-add-item-btn" disabled={loading}>
                    Добавить флаг
                  </button>
                </div>
                {formData.states.flags.map((flag, index) => (
                  <div key={index} className="ismod-item">
                    <div className="modification-fields">
                      <div className="ismod-form-group">
                        <label className="ismod-label">Флаг для изменения:</label>
                        <CustomSelect value={flag.flag_id} onChange={(e) => updateFlag(index, 'flag_id', e.target.value)} className="app-select" disabled={loading} required>
                          <option value="">Выберите флаг</option>
                          {novel?.states?.flags?.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </CustomSelect>
                      </div>
                      <div className="ismod-form-group">
                        <label className="ismod-label">Причина изменения:</label>
                        <textarea value={flag.change_reason_prompt} onChange={(e) => updateFlag(index, 'change_reason_prompt', e.target.value)} className="ismod-textarea" disabled={loading} rows={2} placeholder="Опишите, почему это состояние должно измениться..." required />
                      </div>
                    </div>
                    <button type="button" onClick={() => updateFlags(formData.states.flags.filter((_, i) => i !== index))} className="ismod-remove-btn" disabled={loading}>Удалить</button>
                  </div>
                ))}
              </div>

              <div className="ismod-section">
                <div className="ismod-section-head">
                  <h4 className="ismod-section-title">Модификации шкал</h4>
                  <button type="button" onClick={() => updateScales([...formData.states.scales, { scale_id: '', change_reason_prompt: '' }])} className="ismod-add-item-btn" disabled={loading}>
                    Добавить шкалу
                  </button>
                </div>
                {formData.states.scales.map((scale, index) => (
                  <div key={index} className="ismod-item">
                    <div className="modification-fields">
                      <div className="ismod-form-group">
                        <label className="ismod-label">Шкала для изменения:</label>
                        <CustomSelect value={scale.scale_id} onChange={(e) => updateScale(index, 'scale_id', e.target.value)} className="app-select" disabled={loading} required>
                          <option value="">Выберите шкалу</option>
                          {novel?.states?.scales?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </CustomSelect>
                      </div>
                      <div className="ismod-form-group">
                        <label className="ismod-label">Причина изменения:</label>
                        <textarea value={scale.change_reason_prompt} onChange={(e) => updateScale(index, 'change_reason_prompt', e.target.value)} className="ismod-textarea" disabled={loading} rows={2} placeholder="Опишите, почему это состояние должно измениться..." required />
                      </div>
                    </div>
                    <button type="button" onClick={() => updateScales(formData.states.scales.filter((_, i) => i !== index))} className="ismod-remove-btn" disabled={loading}>Удалить</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="ismod-form-group">
              <label className="ismod-label">Тип модификации:</label>
              <CustomSelect value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="app-select" disabled={loading} required>
                <option value="continuous">Непрерывная</option>
                <option value="single">Одиночная</option>
              </CustomSelect>
              <small className="form-help">
                {formData.type === 'continuous' ? 'Применяется каждый раз при проигрывании сцены' : 'Применяется только при срабатывании концовки'}
              </small>
            </div>

            <div className="ismod-form-group">
              <label className="ismod-label">Условия:</label>
              <Conditionals
                flags={novel?.states?.flags || []}
                scales={novel?.states?.scales || []}
                endings={getAllEndingsFromNovel(novel)}
                value={formData.conditionals}
                onChange={(conditionals) => setFormData({ ...formData, conditionals })}
                disabled={loading}
              />
            </div>

            <div className="ismod-form-actions">
              <button type="button" onClick={handleSaveModifyingState} disabled={loading} className="ismod-btn-primary">
                {loading ? 'Загрузка...' : 'Сохранить'}
              </button>
              <button type="button" onClick={() => { close(); setError(null); }} disabled={loading} className="ismod-btn-cancel">
                Отменить
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ismod-list">
        {scene?.modifying_states?.length > 0 ? (
          scene.modifying_states.map((modifyingState, index) => (
              <div key={modifyingState.id || index} className="ismod-card">
                <div className="ismod-card-head">
                  <h4 className="ismod-card-name">Модификация состояния {index + 1}</h4>
                  <div className="ismod-card-actions">
                    <button className="ismod-btn-edit" onClick={() => handleEditModifyingStateClick(modifyingState, index)} disabled={loading}>Редактировать</button>
                    <button className="ismod-btn-delete" onClick={() => handleDeleteModifyingState(index)} disabled={loading}>Удалить</button>
                  </div>
                </div>
                <div className="ismod-card-body">
                  <div className="modifying-state-details">
                    <p><strong>Тип модификации:</strong> {modifyingState.type === 'continuous' ? 'Непрерывная' : 'Одиночная'}</p>
                    {modifyingState.states?.flags && modifyingState.states.flags.length > 0 && (
                      <div>
                        <p><strong>Модификации флагов:</strong></p>
                        <ul>
                          {modifyingState.states.flags.map((flag, flagIndex) => (
                            <li key={flagIndex}>{getFlagName(flag.flag_id)}: {flag.change_reason_prompt}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {modifyingState.states?.scales && modifyingState.states.scales.length > 0 && (
                      <div>
                        <p><strong>Модификации шкал:</strong></p>
                        <ul>
                          {modifyingState.states.scales.map((scale, scaleIndex) => (
                            <li key={scaleIndex}>{getScaleName(scale.scale_id)}: {scale.change_reason_prompt}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {modifyingState.conditionals?.length > 0 && (
                      <p><strong>Условия:</strong> {modifyingState.conditionals.length}</p>
                    )}
                  </div>
                </div>
              </div>
          ))
        ) : (
          <div className="ismod-empty">
            <p>Для этой сцены пока не настроены модификации состояний.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveSceneModifyingStates;
